/** True when pathname is the admin dashboard home (not a sub-route). */
import { buildWorkspaceIdentity } from "../ai/workspaceContext";
import { isDashboardPath, isNexusChatPath, isNexusHomePath, ROUTES } from "./routes";

export function isAdminDashboardPath(pathname) {
  return isDashboardPath(pathname);
}

export function isAdminNexusChatPath(pathname) {
  return isNexusChatPath(pathname);
}

export function isAdminNexusHomePath(pathname) {
  return isNexusHomePath(pathname);
}

export function buildPlatformCommandContext({ currentRoute = ROUTES.dashboard } = {}) {
  return {
    surface: "command_center",
    currentRoute,
  };
}

export function buildDashboardContext({ organizations = [], recentRoutes = [] } = {}) {
  const identity = buildWorkspaceIdentity();
  const dimWord = identity.is_classic ? "pillars" : "sections";
  const orgs = Array.isArray(organizations) ? organizations : [];
  const reportReady = orgs.filter((o) => o?.reportReady);
  const withInvites = orgs.filter((o) => (o?.inviteCount ?? o?.invites ?? 0) > 0);

  return {
    page: "dashboard",
    scope: {
      label: "Dashboard",
      text: "Admin home — portfolio overview, workflows, and quick navigation",
    },
    kpis: {
      totalCompanies: orgs.length,
      reportReadyCount: reportReady.length,
      companiesWithInvites: withInvites.length,
    },
    companies: orgs.slice(0, 20).map((o) => ({
      name: o.name || o.company_name || null,
      reportReady: !!o.reportReady,
      overallScore: o.overallScore ?? o.overall_score ?? null,
      completionRate: o.completionRate ?? o.completion_rate ?? null,
    })),
    recentAdminPages: recentRoutes.slice(0, 6),
    hints: [
      `Ask about portfolio-wide completion, weakest ${dimWord}, or which companies need attention.`,
      "Use workflows to send invites or open executive, team, and company reports.",
    ],
  };
}

/** Page-specific JSON context for Command center insight queries. */

function summarizeCompanyRow(row, execStatus) {
  const status = execStatus?.[row._id];
  return {
    company_name: row.company_name || null,
    industry: row.industry || null,
    company_size: row.company_size || null,
    revenue_last_year: row.revenue_last_year || null,
    ebita: row.ebita || null,
    company_ownership: row.company_ownership || null,
    invited_by: row.invited_by?.name || row.invited_by?.email || null,
    assigned_owner: row.assigned_owner?.name || row.assigned_owner?.email || null,
    paused: row.is_paused === "Y",
    executive_summary_ready: status?.ready ?? null,
    executive_summary_members: status?.num_members ?? null,
  };
}

export function buildCompanyProfileContext(companies, filterText, execStatusByOrg = {}) {
  const q = (filterText || "").trim().toLowerCase();
  const filtered = q
    ? companies.filter((row) =>
        [
          row.company_name,
          row.revenue_last_year,
          row.revenue_last_12months,
          row.industry,
          row.company_size,
          row.ebita,
          row.company_ownership,
          row.assigned_owner?.name,
          row.assigned_owner?.email,
        ].some((v) => String(v || "").toLowerCase().includes(q))
      )
    : companies;

  const paused = filtered.filter((r) => r.is_paused === "Y").length;
  const execReady = filtered.filter((r) => execStatusByOrg[r._id]?.ready).length;

  return {
    page: "company_profile",
    scope: {
      label: "Company Profile",
      text: "Company workspace records, logos, pause state, and executive summary readiness",
    },
    filterText: filterText || "",
    kpis: {
      totalCompanies: companies.length,
      visibleCount: filtered.length,
      pausedCount: paused,
      executiveSummaryReadyCount: execReady,
    },
    companies: filtered.slice(0, 40).map((row) => summarizeCompanyRow(row, execStatusByOrg)),
    companyNames: filtered.map((r) => r.company_name).filter(Boolean).slice(0, 50),
    hints: [
      "Ask about industries, revenue bands, paused companies, or which companies can generate executive summaries.",
      "Executive summary ready means the team has completed enough surveys for a PDF.",
    ],
  };
}

export function buildReportProfilesContext({
  profiles,
  catalog,
  reportBase,
  showEditor,
  editingProfile,
  embedded = false,
}) {
  const companyProfiles = profiles.filter((p) => p.is_team_base !== true);
  const teamProfiles = profiles.filter((p) => p.is_team_base === true);
  const active = reportBase === "team" ? teamProfiles : companyProfiles;

  const serializeProfile = (p) => ({
    profile_id: p.profile_id || p._id || null,
    name: p.name || null,
    description: (p.description || "").slice(0, 200) || null,
    target_role: p.target_role || null,
    is_system: !!p.is_system,
    is_default: !!p.is_default,
    block_count: (p.blocks || []).length,
    block_ids: (p.blocks || []).map((b) => b.block_id || b.id).filter(Boolean).slice(0, 30),
  });

  return {
    page: "report_profiles",
    surface: embedded ? "studio" : "admin",
    currentRoute: embedded ? `${ROUTES.studio}?lens=reports` : ROUTES.reportProfiles,
    pagePath: embedded ? `${ROUTES.studio}?lens=reports` : ROUTES.reportProfiles,
    activeLens: embedded ? "reports" : null,
    scope: {
      label: embedded ? "Studio · Report profiles" : "Report Profiles",
      text: "Configurable PDF report templates (company vs team) built from content blocks",
    },
    reportBase,
    editorOpen: !!showEditor,
    editingProfile: editingProfile
      ? serializeProfile(editingProfile)
      : null,
    kpis: {
      companyProfileCount: companyProfiles.length,
      teamProfileCount: teamProfiles.length,
      catalogBlockCount: catalog.length,
      visibleProfileCount: active.length,
    },
    visibleProfiles: active.map(serializeProfile),
    blockCatalog: catalog.slice(0, 40).map((b) => ({
      block_id: b.block_id,
      name: b.name,
      company_base_only: !!b.company_base_only,
      team_base_only: !!b.team_base_only,
    })),
    hints: [
      "Company reports include individual role breakdown; team reports include pillar rankings.",
      "System/default profiles cannot be deleted. Blocks are assigned per profile in the editor.",
    ],
  };
}

function questionsInStep(step) {
  if (!step) return [];
  if (Array.isArray(step.questions)) return step.questions;
  const fromSections = [];
  for (const section of step.sections || []) {
    for (const q of section.questions || []) {
      if (q?.id) fromSections.push(q);
    }
  }
  return fromSections;
}

function countQuestionsInSteps(steps) {
  let n = 0;
  for (const step of steps || []) {
    n += questionsInStep(step).length;
  }
  return n;
}

export function buildMailManagerContext({
  templates = [],
  selectedTemplateId = null,
  editorTemplate = null,
  view = "editor",
  audiences = [],
  allowPerRoleEmailTemplates = true,
  isClassicEmailWorkspace = false,
}) {
  const selected = templates.find((t) => t.id === selectedTemplateId) || editorTemplate;
  return {
    page: "mail_manager",
    surface: "admin",
    currentRoute: ROUTES.adminEmailTemplates,
    pagePath: ROUTES.adminEmailTemplates,
    scope: {
      label: "Mail Manager",
      text: "Invitation and reminder email templates, audiences, routing, and send log",
    },
    kpis: {
      templateCount: templates.length,
      audienceCount: audiences.length,
      perRoleTemplatesEnabled: !!allowPerRoleEmailTemplates,
      classicEmailPack: !!isClassicEmailWorkspace,
    },
    mailManager: {
      activeView: view,
      selectedTemplateId: selected?.id || selectedTemplateId || null,
      selectedTemplateLabel: selected?.label || selected?.name || null,
      templateTypes: templates.slice(0, 30).map((t) => ({
        id: t.id,
        label: t.label || t.name || null,
        type: t.template_type || t.type || null,
      })),
    },
    hints: [
      "Templates support merge tokens for participant and company fields.",
      "Routing maps participant role or audience to the right template on send.",
      "Reminder cadence is configured alongside global scheduler rules.",
    ],
  };
}

export function buildWorkspaceBrandingContext({ workspace = null, productSettings = null } = {}) {
  const ps = productSettings || workspace?.product_settings || {};
  return {
    page: "workspace_branding",
    surface: "admin",
    currentRoute: ROUTES.branding,
    pagePath: ROUTES.branding,
    scope: {
      label: "Workspace branding & product settings",
      text: "Product name, logo, colors, company model (portfolio vs single org), and LITE policy",
    },
    kpis: {
      litePolicy: ps.lite_policy || workspace?.lite_invite_available ? "enabled" : "disabled",
      companyModel: ps.company_model || workspace?.company_model || "portfolio",
    },
    hints: [
      "Product settings control LITE on invites and whether Studio shows LITE tools.",
      "Branding applies to participant assessment chrome and PDF reports.",
    ],
  };
}

export function buildAssessmentFormContext({
  fullSteps,
  liteSteps,
  variant,
  currentStep,
  surveyId,
}) {
  const steps = variant === "lite" ? liteSteps : fullSteps;
  const step = steps[currentStep - 1];

  const stepSummaries = steps.map((s, i) => ({
    step: s.step ?? i + 1,
    title: s.title || null,
    questionCount: countQuestionsInSteps([s]),
  }));

  const currentQuestions = questionsInStep(step)
    .slice(0, 25)
    .map((q) => ({
      id: q.id,
      text: (q.text || "").slice(0, 280),
      type: q.type || null,
    }));

  return {
    page: "assessment_form",
    scope: {
      label: "Assessment form",
      text: "Survey question templates (Full vs LITE) shown to participants",
    },
    surveyId: surveyId || null,
    variant,
    currentStep,
    totalSteps: steps.length,
    currentStepTitle: step?.title || null,
    kpis: {
      fullStepCount: fullSteps.length,
      liteStepCount: liteSteps.length,
      fullQuestionCount: countQuestionsInSteps(fullSteps),
      liteQuestionCount: countQuestionsInSteps(liteSteps),
      currentStepQuestionCount: currentQuestions.length,
    },
    stepSummaries,
    currentStepQuestions: currentQuestions,
    hints: [
      "Full assessment is multi-step; LITE is a single-page shortened survey.",
      "Admins can edit question wording from this page; changes apply to the live survey template.",
    ],
  };
}
